import React, { useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from './api'
import { Arrow, Logo, Spinner } from './components'

function AuthShell({ title, subtitle, children, footer }) {
  return <div className="auth-page">
    <div className="auth-left"><Logo/><div><span className="eyebrow">THE SAFETY NET FOR YOUR CHANNEL</span><blockquote>“I thought YouTube was the backup. Then my channel disappeared.”</blockquote><p>— the sentence this product exists to prevent</p></div><small>No Google login. Your files stay private.</small></div>
    <main className="auth-card"><Logo/><div className="auth-heading"><h1>{title}</h1><p>{subtitle}</p></div>{children}<div className="auth-footer">{footer}</div></main>
  </div>
}

export function SignupPage({ onAuthenticated }) {
  const [params] = useSearchParams()
  const plan = params.get('plan') || 'studio'
  const billing = params.get('billing') || 'yearly'
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkEmail, setCheckEmail] = useState(false)
  const navigate = useNavigate()

  async function submit(event) {
    event.preventDefault(); setError(''); setLoading(true)
    try {
      const result = await api('/api/auth/signup', { method: 'POST', body: JSON.stringify(form) })
      if (result.devVerificationToken) {
        await api('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token: result.devVerificationToken }) })
        await onAuthenticated()
        navigate(`/pricing?plan=${plan}&billing=${billing}`)
      } else setCheckEmail(true)
    } catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  if (checkEmail) return <AuthShell title="Check your inbox." subtitle="Click the verification link, then choose your backup plan." footer={<Link to="/login">Already verified? Log in</Link>}><div className="email-sent"><span>→</span><p>We sent a private verification link to <b>{form.email}</b>.</p></div></AuthShell>

  return <AuthShell title="Create your vault." subtitle="Payment comes next. Channel details come after payment." footer={<>Already have an account? <Link to="/login">Log in</Link></>}>
    <form className="auth-form" onSubmit={submit}>
      <label>Email address<input type="email" required autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@channel.com"/></label>
      <label>Password<input type="password" required minLength="10" autoComplete="new-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="At least 10 characters"/></label>
      {error && <div className="form-error">{error}</div>}
      <button className="button button-primary button-wide" disabled={loading}>{loading ? <Spinner/> : <>Choose my backup plan <Arrow/></>}</button>
      <small>By continuing, you agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</small>
    </form>
  </AuthShell>
}

export function LoginPage({ onAuthenticated }) {
  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const requestedNext = params.get('next')
  const next = requestedNext?.startsWith('/') && !requestedNext.startsWith('//') ? requestedNext : '/app'
  async function submit(event) {
    event.preventDefault(); setError(''); setLoading(true)
    try { await api('/api/auth/login', { method: 'POST', body: JSON.stringify(form) }); await onAuthenticated(); navigate(next) }
    catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }
  return <AuthShell title="Welcome back." subtitle="Your vault is exactly where you left it." footer={<>New here? <Link to="/signup">Create an account</Link></>}>
    <form className="auth-form" onSubmit={submit}>
      <label>Email address<input type="email" required autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}/></label>
      <label>Password<input type="password" required autoComplete="current-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}/></label>
      {error && <div className="form-error">{error}</div>}
      <button className="button button-primary button-wide" disabled={loading}>{loading ? <Spinner/> : <>Open my vault <Arrow/></>}</button>
    </form>
  </AuthShell>
}

export function VerifyEmailPage({ onAuthenticated }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState('Verifying your email…')
  React.useEffect(() => {
    const token = new URLSearchParams(location.search).get('token')
    if (!token) { setStatus('This verification link is incomplete.'); return }
    api('/api/auth/verify-email', { method: 'POST', body: JSON.stringify({ token }) })
      .then(async () => { await onAuthenticated(); navigate('/pricing', { replace: true }) })
      .catch(err => setStatus(err.message))
  }, [location.search, navigate, onAuthenticated])
  return <AuthShell title="One moment." subtitle={status} footer={<Link to="/login">Return to login</Link>}><div className="verify-spinner"><Spinner/></div></AuthShell>
}
